import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { ProductImage } from '@/components/blocks/ProductImage';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { Linkable } from '@/components/blocks/Linkable';
import { useProductExtractMerchandisingAssociations } from '@/utils/productExtractMerchandisingAssociations';
import { ColorScroller } from '@/components/blocks/ProductDetails/ColorScroller';

const ProductThumbnail = ({
	href,
	thumbnailUrl,
	isSelected = false,
}: {
	href?: string;
	thumbnailUrl: string;
	isSelected?: boolean;
}) => (
	<Linkable href={href}>
		<RenderProductImg
			fullImageRaw={thumbnailUrl}
			render={(src) => (
				<ProductImage
					sx={{
						mt: '5px !important',
						mb: '10px',
						width: '70px',
						...(isSelected
							? {
									border: '2px solid',
									borderColor: 'primary.main',
									boxSizing: 'border-box',
							  }
							: {}),
					}}
					src={src}
					alt={'mm'}
					ribbons={[]}
					isThumbnail={false}
					isSelected={isSelected}
				/>
			)}
		/>
	</Linkable>
);

export const ColorProduct: FC = () => {
	const { product } = useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useSkuListTable>;

	const color = useMemo(() => {
		const atrrs = product?.attributes || [];
		const colorValues = [
			findProductAttribute(atrrs, 'Color1')?.value,
		  findProductAttribute(atrrs, 'Color2')?.value,
			].filter((value): value is string => Boolean(value));
			return Array.from(new Set(colorValues)).join(' / ');
	       }, [product?.attributes]);

	const currentPartNumber = useMemo(() => product?.partNumber || '', [product?.partNumber]);

	const { productVariantColors } = useProductExtractMerchandisingAssociations(product);

	return (
		<Stack sx={{ marginTop: '0px', mb: '10px' }}>
			{color ? (
				<Typography
					sx={{
						fontSize: 'var(--font-size-default)',
						fontWeight: 'bold',
						color: '#2C2C2C',
					}}
				>
					{'Color: '}
					<Typography
						sx={{
							fontSize: 'var(--font-size-default)',
							fontWeight: 'normal',
							color: '#2C2C2C',
							display: 'inline',
						}}
					>
						{color}
					</Typography>
				</Typography>
			) : null}

			<ColorScroller activeKey={currentPartNumber}>
				{false ? (
					<>
						{Array.from({
							length: 2,
						}).map((_, index) => (
							<Box key={index} sx={{ flexShrink: 0, minWidth: '70px' }}>
								<Skeleton
									variant="rectangular"
									width={70}
									height={70}
									sx={{
										mt: '5px !important',
										mb: '10px',
										width: '70px',
									}}
								/>
							</Box>
						))}
					</>
				) : productVariantColors.length > 0 ? (
					productVariantColors.map((variant) => {
						const isSelected = variant.partNumber === currentPartNumber;
						return (
							<Box
								key={variant.partNumber}
								data-selected={isSelected ? 'true' : undefined}
								sx={{ flexShrink: 0, minWidth: '70px', scrollSnapAlign: 'start' }}
							>
								<ProductThumbnail
									thumbnailUrl={variant.thumbnail}
									isSelected={isSelected}
									href={variant.seo?.href}
								/>
							</Box>
						);
					})
				) : (
					<ProductThumbnail thumbnailUrl={product?.thumbnail || ''} isSelected />
				)}
			</ColorScroller>
		</Stack>
	);
};
